import React from 'react';
import { Chip } from '@mui/material';
import { i18n, Text, Theme, tokens } from '@simplybenefits/sb-ui-library';
import { helpers } from '@simplybenefits/sb-utils';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { 
    Add as AddIcon,
    Remove as RemoveIcon 
} from '@mui/icons-material';
import CoverageBlock from '../CoverageBlock.js';
import CoverageBlockFull from '../CoverageBlockFull.js';

/**
 *
 * @param {*} props
 */
export default function Paramedicals ({ classes, expanded, paramedicals, handleChange, defineDeductibleVerbiage, showDependentWarning, waivedEHC, hasDeductible }) {
    return (
        <div style={ classes.block }>
            <Accordion
                square
                onChange={handleChange('paramedicals')}
                style={{ boxShadow: 'none' }}
                disableGutters={true}
            >
                <AccordionSummary
                    classes={{
                        content: classes.panelContent,
                        expanded: classes.expanded,
                        root: classes.root
                    }}
                    expandIcon={expanded.includes('paramedicals') ? <RemoveIcon /> : <AddIcon />}
                    sx={classes.accordionSummary}
                >
                    <Text medium bold>Paramedicals</Text>
                    {
                        waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "0 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                    }
                </AccordionSummary>
                <AccordionDetails style={classes.panelDetails}>
                    {
                        paramedicals.isEligible !== 1 ?
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    You are currently ineligible for this benefit, if this is a mistake please contact your administrator.
                                </Text>
                            </div>
                            : null
                    }

                    {
                        showDependentWarning &&
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    You have waived this benefit for your dependents.
                                </Text>
                            </div>
                    }

                    <div style={classes.row}>
                        <Text small bold margin="10px 0px 10px 0px">
                            {`You have ${ paramedicals.reimbursement }% coverage${ paramedicals.combinedMaximum !== 0 ? ` up to the below maximums` : `` } per listed service${ paramedicals.isCombinedMaximum === 1 || paramedicals.isByPractitionerToCombinedMaximum === 1 ? ` to a combined maximum of $${ helpers.formatNumber(paramedicals.combinedMaximum) } which is renewed each calendar year.` : ', which is renewed each calendar year.' }` }
                        </Text>
                    </div>

                    {
                        (paramedicals.familyCombinedMaximum && paramedicals.familyCombinedMaximum !== 0) ?
                            <div style={classes.row}>
                                <Text small bold margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.combinedFamilyMaximum, { combinedMaximum: helpers.formatNumber(paramedicals.familyCombinedMaximum || 0) }) }
                                </Text>
                            </div>
                            :
                            null
                    }

                    {
                        hasDeductible ?
                            defineDeductibleVerbiage()
                            : null
                    }

                    <React.Fragment>
                        <CoverageBlock
                            title="Benefit"
                            value="Maximum"
                            type="header"
                            isCombined={paramedicals.combinedMaximum !== 0}
                        />
                        <CoverageBlockFull
                            coverage={paramedicals}
                            isCombined={paramedicals.combinedMaximum !== 0}
                            hasMentalWellnessGrouped={paramedicals.hasMentalWellnessGrouped}
                        />
                    </React.Fragment>

                    {
                        Boolean(paramedicals.hasMentalWellnessGrouped) &&
                            <>
                                <div style={classes.row}>
                                    <Text small bold margin="10px 0px 10px 0px">
                                        { `*You have ${ paramedicals.mentalWellnessReimbursement }% coverage for Mental Wellness (Social Worker, Psychologist, Psychotherapist) to a maximum of $${ helpers.formatNumber(paramedicals.mentalWellnessMaximum) } which is renewed each calendar year.` }
                                    </Text>
                                </div>

                                <Text small margin="0px 0px 10px 0px">
                                    { i18n.t(tokens.dropdowns.benefits.socialWorker) }*
                                </Text>
                                <Text small margin="0px 0px 10px 0px">
                                    { i18n.t(tokens.dropdowns.benefits.psychologist) }*
                                </Text>
                                <Text small margin="0px 0px 10px 0px">
                                    { i18n.t(tokens.dropdowns.benefits.psychotherapist) }*
                                </Text>
                            </>
                    }
                    {
                        paramedicals.hasFunctionalTreatmentGrouped ?
                            <>
                                <div style={classes.row}>
                                    <Text small bold margin="10px 0px 10px 0px">
                                        { i18n.t(tokens.labels.coverage.functionalTreatmentMaximum, { functionalTreatmentReimbursement: paramedicals.functionalTreatmentReimbursement, functionalTreatmentMaximum: paramedicals.functionalTreatmentMaximum })}
                                    </Text>
                                </div>

                                <Text small margin="0px 0px 10px 0px">
                                    { i18n.t(tokens.dropdowns.benefits.osteopath) }*
                                </Text>
                                <Text small margin="0px 0px 10px 0px">
                                    { i18n.t(tokens.dropdowns.benefits.chiropodist) }*
                                </Text>
                                <Text small margin="0px 0px 10px 0px">
                                    { i18n.t(tokens.dropdowns.benefits.podiatrist) }*
                                </Text>
                            </>
                            :
                            null
                    }

                    <Text small bold margin="10px 0px 0px 0px">
                        <>
                            { i18n.t(tokens.labels.coverage.paramedicalTelusLookupOne) }
                            { 
                                <a href="https://plus.telushealth.co/page/eclaims/discover/" target="_blank" rel="noreferrer" style={{ color: Theme.colors.skyBlue }}>
                                    {i18n.t(tokens.labels.coverage.paramedicalTelusLookupTwo)}
                                </a>
                            }
                            { i18n.t(tokens.labels.coverage.paramedicalTelusLookupThree) }
                        </>
                    </Text> 

                    <Text small bold margin="10px 0px 0px 0px">
                        Paramedical services have a Reasonable and Customary amount and a limit of one visit per day. Claim reimbursements may be subject to deductions based on your plan design. If you have any questions, please contact your plan administrator.
                    </Text>
                </AccordionDetails>
            </Accordion>
        </div>
    );
}